import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { RESULTADO_JULGAMENTO } from '@/dominio/ResultadoJulgamento';
import SituacaoJulgamentoProcessoBusiness from '@/dominio/SituacaoJulgamentoProcessoBusiness';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import Configuracao from '@/util/Configuracao';
import StringUtil from '@/util/StringUtil';
import ReactComponent from '../ReactComponent';
import IconeJulgar from './icones/IconeJulgar';
import ModalConfirmacao from './ModalConfirmacao';
import ResultadosJulgamentoProcessoNaoJulgado from './ResultadosJulgamentoProcessoNaoJulgado';

/**
 * @readonly
 * @constant
 * @enum {Object.<string, Object>}
 */
// prettier-ignore
export const BOTAO_RESULTADO_APRECIACAO = {
    JULGADO_OBSERVACAO:     { id: 'JULGADO_OBSERVACAO',     descricao: 'Julgar com observação'        },
    JULGADO_ALTERACAO_VOTO: { id: 'JULGADO_ALTERACAO_VOTO', descricao: 'Julgar com alteração de voto' },
    JULGADO:                { id: 'JULGADO',                descricao: 'Julgar'                       }
};

class AlterarSituacaoJulgamento extends ReactComponent {
    openConfirmarAlteracaoSituacao = false;
    novaSituacao = null;
    novaVista = null;
    novoTipoResultadoJulgamento = null;

    constructor(props) {
        super(props);

        makeObservable(this, {
            openConfirmarAlteracaoSituacao: observable,
            novaSituacao: observable,
            novaVista: observable,
            novoTipoResultadoJulgamento: observable,
            registro: computed,
            isSessaoExclusivamenteVirtual: computed,
            isProcessoEmJulgamentoVirtual: computed,
            loteProcessos: computed,
            handleClickConfirmar: action,
            limparInformacoesEmMemoria: action,
            handleCloseConfirmarAlteracaoSituacao: action,
            handleClickJulgar: action,
            julgarLote: action
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    get isSessaoExclusivamenteVirtual() {
        return SituacaoJulgamentoProcessoBusiness.isProcessoPautadoEmJulgamentoExclusivamenteVirtual(
            this.sessaoStore.sessaoSelecionada.tipoJulgamento
        );
    }

    get isProcessoEmJulgamentoVirtual() {
        if (!this.registro?.tipoJulgamento) {
            return false;
        }

        return SituacaoJulgamentoProcessoBusiness.isProcessoEmJulgamentoVirtual(
            this.registro.tipoJulgamento,
            this.registro.remessaJulgamento
        );
    }

    get loteProcessos() {
        return this.observableProps.loteProcessos || [];
    }

    handleClickConfirmar = (novaSituacao, novaVista) => {
        this.openConfirmarAlteracaoSituacao = true;
        this.novaSituacao = novaSituacao;
        this.novaVista = novaVista;
        this.novoTipoResultadoJulgamento = this.salaSessaoStore.getTipoResultadoJulgamento(novaSituacao);
    };

    limparInformacoesEmMemoria = () => {
        this.novaSituacao = null;
        this.novaVista = null;
        this.novoTipoResultadoJulgamento = null;
    };

    handleCloseConfirmarAlteracaoSituacao = () => {
        this.openConfirmarAlteracaoSituacao = false;
        this.limparInformacoesEmMemoria();
    };

    handleClickJulgar = async (idTipoResultadoJulgamento, vistas) => {
        let tipoResultadoJulgamento = this.salaSessaoStore.getTipoResultadoJulgamento(idTipoResultadoJulgamento);
        if (this.registro) {
            await this.julgarProcesso(tipoResultadoJulgamento, vistas);
        } else {
            await this.julgarLote(tipoResultadoJulgamento, vistas);
        }
        this.limparInformacoesEmMemoria();
    };

    async julgarProcesso(tipoResultadoJulgamento, vistas) {
        const situacaoInicialResultadoJulgamento = this.registro.resultadoJulgamento.codigo;
        if (situacaoInicialResultadoJulgamento === RESULTADO_JULGAMENTO.VISTA_REGIMENTAL.codigo) {
            await this.processoService.removerVistaRegimental(this.registro);
            runInAction(() => (this.registro.informacoesAdicionais.VistaRegimental = undefined));
        }
        let sucesso = await this.salaSessaoStore.julgar(this.registro, tipoResultadoJulgamento, vistas);
        if (sucesso) {
            if (
                this.registro.resultadoJulgamento &&
                this.registro.resultadoJulgamento.codigo === RESULTADO_JULGAMENTO.VISTA_REGIMENTAL.codigo
            ) {
                await this.salaSessaoStore.recarregarProcesso(this.registro);
            }
            this.mensagemStore.abrirMensagemSucesso(
                'Processo ' +
                    this.registro.processo.numeroCompleto +
                    ' ' +
                    StringUtil.substituirPrimeiraLetraPorMinuscula(tipoResultadoJulgamento.descricao)
            );
        } else {
            this.mensagemStore.abrirMensagemErro(
                'Ocorreu um erro ao julgar o processo: ' + this.registro.processo.numeroCompleto
            );
        }
    }

    async julgarLote(tipoResultadoJulgamento, vistas) {
        if (this.loteProcessos && this.loteProcessos.length > 0) {
            await this.salaSessaoStore.julgarProcessos(this.loteProcessos, tipoResultadoJulgamento, vistas);
            this.salaSessaoStore.recarregarListaProcessos();
        } else {
            this.mensagemStore.abrirMensagemAlerta('Selecione um ou mais processos para apreciar.');
        }
    }

    getConteudoNovoTipoJulgamento() {
        if (
            this.registro.processo.origem === Configuracao.ORIGEM_PJE &&
            // @ts-ignore
            this.novoTipoResultadoJulgamento?.numeroMovimentoCnj
        ) {
            const { numeroMovimentoCnj, descricao } = this.novoTipoResultadoJulgamento;
            const textoNumeroMovimentoCnj = numeroMovimentoCnj ? `${numeroMovimentoCnj} - ` : '';
            return `Observação: O movimento será alterado para ${textoNumeroMovimentoCnj}${descricao}.`;
        } else {
            return 'Atenção: verifique o Código de Decisão após a mudança do resultado de julgamento.';
        }
    }

    renderAlteracaoIndividual() {
        const { codigo, julgado } = this.registro.resultadoJulgamento;
        const processoEmJulgamentoVirtual = !!(
            this.isSessaoExclusivamenteVirtual || this.isProcessoEmJulgamentoVirtual
        );
        const mensagemBotaoDesabilitado = 'Processo já está julgado nessa situação';
        const botaoJulgarDesabilitado = codigo === RESULTADO_JULGAMENTO.JULGADO.codigo;
        const botaoJulgarComObservacaoDesabilitado = codigo === RESULTADO_JULGAMENTO.JULGADO_OBSERVACAO.codigo;
        const botaoJulgarComAlteracaoDeVotoDesabilitado = codigo === RESULTADO_JULGAMENTO.JULGADO_ALTERACAO_VOTO.codigo;

        return (
            <div style={{ display: 'flex' }}>
                {!processoEmJulgamentoVirtual && (!this.observableProps.modoEdicao || julgado === true) && (
                    <>
                        <Tooltip
                            placement="top"
                            title={
                                <Typography>
                                    {botaoJulgarDesabilitado
                                        ? mensagemBotaoDesabilitado
                                        : BOTAO_RESULTADO_APRECIACAO.JULGADO.descricao}
                                </Typography>
                            }
                        >
                            <Box>
                                <IconButton
                                    onClick={() =>
                                        this.observableProps.modoEdicao
                                            ? this.handleClickConfirmar(RESULTADO_JULGAMENTO.JULGADO.codigo)
                                            : this.handleClickJulgar(RESULTADO_JULGAMENTO.JULGADO.codigo)
                                    }
                                    style={{ padding: '8px', marginRight: '4px' }}
                                    size="large"
                                    disabled={botaoJulgarDesabilitado}
                                >
                                    <IconeJulgar
                                        letra={RESULTADO_JULGAMENTO.JULGADO.letra}
                                        cor={botaoJulgarDesabilitado ? grey[300] : 'black'}
                                    />
                                </IconButton>
                            </Box>
                        </Tooltip>
                        <Tooltip
                            placement="top"
                            title={
                                <Typography>
                                    {botaoJulgarComObservacaoDesabilitado
                                        ? mensagemBotaoDesabilitado
                                        : BOTAO_RESULTADO_APRECIACAO.JULGADO_OBSERVACAO.descricao}
                                </Typography>
                            }
                        >
                            <Box>
                                <IconButton
                                    onClick={() =>
                                        this.observableProps.modoEdicao
                                            ? this.handleClickConfirmar(RESULTADO_JULGAMENTO.JULGADO_OBSERVACAO.codigo)
                                            : this.handleClickJulgar(RESULTADO_JULGAMENTO.JULGADO_OBSERVACAO.codigo)
                                    }
                                    style={{ padding: '8px', marginRight: '4px' }}
                                    size="large"
                                    disabled={botaoJulgarComObservacaoDesabilitado}
                                >
                                    <IconeJulgar
                                        letra={RESULTADO_JULGAMENTO.JULGADO_OBSERVACAO.letra}
                                        cor={botaoJulgarComObservacaoDesabilitado ? grey[300] : 'black'}
                                    />
                                </IconButton>
                            </Box>
                        </Tooltip>
                        <Tooltip
                            placement="top"
                            title={
                                <Typography>
                                    {botaoJulgarComAlteracaoDeVotoDesabilitado
                                        ? mensagemBotaoDesabilitado
                                        : BOTAO_RESULTADO_APRECIACAO.JULGADO_ALTERACAO_VOTO.descricao}
                                </Typography>
                            }
                        >
                            <Box>
                                <IconButton
                                    onClick={() =>
                                        this.observableProps.modoEdicao
                                            ? this.handleClickConfirmar(
                                                  RESULTADO_JULGAMENTO.JULGADO_ALTERACAO_VOTO.codigo
                                              )
                                            : this.handleClickJulgar(RESULTADO_JULGAMENTO.JULGADO_ALTERACAO_VOTO.codigo)
                                    }
                                    style={{ padding: '8px', marginRight: '4px' }}
                                    size="large"
                                    disabled={botaoJulgarComAlteracaoDeVotoDesabilitado}
                                >
                                    <IconeJulgar
                                        letra={RESULTADO_JULGAMENTO.JULGADO_ALTERACAO_VOTO.letra}
                                        cor={botaoJulgarComAlteracaoDeVotoDesabilitado ? grey[300] : 'black'}
                                    />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    </>
                )}
                {(!this.observableProps.modoEdicao || julgado === false) && (
                    <ResultadosJulgamentoProcessoNaoJulgado
                        registro={this.registro}
                        callbackApreciar={(novaSituacao, vistas) =>
                            this.observableProps.modoEdicao
                                ? this.handleClickConfirmar(novaSituacao, vistas)
                                : this.handleClickJulgar(novaSituacao, vistas)
                        }
                        permitirFixar={!this.observableProps.modoEdicao}
                        zIndex={'1500'}
                    />
                )}
                <ModalConfirmacao
                    titulo={'Confirmação de mudança'}
                    open={this.openConfirmarAlteracaoSituacao}
                    callbackCancelar={this.handleCloseConfirmarAlteracaoSituacao}
                    callbackConfirmar={() => this.handleClickJulgar(this.novaSituacao, this.novaVista)}
                >
                    <Typography display={'inline'}>
                        Tem certeza que deseja alterar a situação do julgamento do processo?
                        <Typography sx={{ mt: 1, fontWeight: 'bold' }}>
                            {this.getConteudoNovoTipoJulgamento()}
                        </Typography>
                    </Typography>
                </ModalConfirmacao>
            </div>
        );
    }

    renderAlteracaoEmLote() {
        const botoesJulgarDesabilitados = this.isSessaoExclusivamenteVirtual;
        const mensagemBotaoDesabilitado = 'Não é possível julgar processos em sessões exclusivamente virtuais.';

        return (
            <div style={{ display: 'flex' }}>
                <Tooltip
                    title={
                        <Typography>
                            {botoesJulgarDesabilitados
                                ? mensagemBotaoDesabilitado
                                : BOTAO_RESULTADO_APRECIACAO.JULGADO.descricao}
                        </Typography>
                    }
                >
                    <Box>
                        <IconButton
                            onClick={() => this.handleClickJulgar(RESULTADO_JULGAMENTO.JULGADO.codigo)}
                            size="large"
                            disabled={botoesJulgarDesabilitados}
                        >
                            <IconeJulgar
                                letra={RESULTADO_JULGAMENTO.JULGADO.letra}
                                cor={botoesJulgarDesabilitados ? grey[300] : 'black'}
                            />
                        </IconButton>
                    </Box>
                </Tooltip>
                <Tooltip
                    title={
                        <Typography>
                            {botoesJulgarDesabilitados
                                ? mensagemBotaoDesabilitado
                                : BOTAO_RESULTADO_APRECIACAO.JULGADO_OBSERVACAO.descricao}
                        </Typography>
                    }
                >
                    <Box>
                        <IconButton
                            onClick={() => this.handleClickJulgar(RESULTADO_JULGAMENTO.JULGADO_OBSERVACAO.codigo)}
                            size="large"
                            disabled={botoesJulgarDesabilitados}
                        >
                            <IconeJulgar
                                letra={RESULTADO_JULGAMENTO.JULGADO_OBSERVACAO.letra}
                                cor={botoesJulgarDesabilitados ? grey[300] : 'black'}
                            />
                        </IconButton>
                    </Box>
                </Tooltip>
                <Tooltip
                    title={
                        <Typography>
                            {botoesJulgarDesabilitados
                                ? mensagemBotaoDesabilitado
                                : BOTAO_RESULTADO_APRECIACAO.JULGADO_ALTERACAO_VOTO.descricao}
                        </Typography>
                    }
                >
                    <Box>
                        <IconButton
                            onClick={() => this.handleClickJulgar(RESULTADO_JULGAMENTO.JULGADO_ALTERACAO_VOTO.codigo)}
                            size="large"
                            disabled={botoesJulgarDesabilitados}
                        >
                            <IconeJulgar
                                letra={RESULTADO_JULGAMENTO.JULGADO_ALTERACAO_VOTO.letra}
                                cor={botoesJulgarDesabilitados ? grey[300] : 'black'}
                            />
                        </IconButton>
                    </Box>
                </Tooltip>
                <ResultadosJulgamentoProcessoNaoJulgado
                    registro={this.registro}
                    callbackApreciar={this.handleClickJulgar}
                    permitirFixar={false}
                    zIndex={'1500'}
                />
            </div>
        );
    }

    render() {
        return this.registro ? this.renderAlteracaoIndividual() : this.renderAlteracaoEmLote();
    }
}

export default inject('stores')(observer(AlterarSituacaoJulgamento));
